import { useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import DetailsTable from 'src/components/DetailsTable';
import EntityStatusCard from 'src/components/EntityStatusCard';
import Iconify from 'src/components/Iconify';
import WarningIcon from 'src/components/WarningIcon';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { RESULTS_TAB_DATA_COUNTS_KEY, WDAC_APP_QUERY_KEY } from 'src/constants/query';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { useWin32AppFileSets, useWin32AppPolicy } from 'src/hooks/wdac';
import WDACResultsService from 'src/services/wdac-results.service';
import { formatDate } from 'src/utils/formatTime';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusLabel,
} from 'src/utils/enums/applicationTrustStatus';
import { isCustomerEnvironmentApplicationEnabled } from 'src/utils/enums/customerEnvironmentApplicationStatus';
import Win32CatalogActions from '../../Win32CatalogActions';
import Win32FileSetImports from '../../Win32FileSetImports';
import Win32TrustedBasePolicies from '../../Win32TrustedBasePolicies';
import TelemetryDevicesModal from '../TelemetryTab/TelemetryDevicesModal';
import DetectedActionsCard from './DetectedActionsCard';

const count = value => (value ?? 0).toLocaleString();

// The warning beside the status and the detected action, for each gap between the trust and the
// artifacts built for it. An installation import rebuilds against the bases the artifacts were last
// built for, or the environment defaults for a first build, so the other two gaps say which was missing.
const ARTIFACTS_GAPS = {
  noImportsEnabled: {
    warning: 'None of your file set imports are enabled, so there are no files for this app.',
    title: 'No file set imports are enabled',
    subtitle: 'Build trusted artifacts to choose which files this trust covers.',
  },
  noBasePolicies: {
    warning:
      'File set imports are enabled, but no managed base policies are selected and the environment has no default base policies, so no security catalog covers them.',
    title: 'No managed base policies are selected',
    subtitle:
      'There are no environment default base policies to build against, so build trusted artifacts to choose them.',
  },
  staleArtifacts: {
    warning:
      'An installation import trusted this app after its artifacts were last generated, and none of the base policies they were built against are still managed, so the security catalog does not cover the new import.',
    title: 'Installation import is not in the trusted artifacts',
    subtitle:
      'Its base policies are no longer managed, so update trusted artifacts with new ones to include the import.',
  },
};

// What this app is, as this environment sees it. The status and note live in the card above rather
// than here, so nothing on this table is editable.
const APP_GENERAL_ROWS = [
  { key: 'publisher', label: 'Publisher', render: app => app.publisher },
  { key: 'version', label: 'Version', render: app => app.version },
  {
    key: 'status',
    label: 'Trust Status',
    render: app => getApplicationTrustStatusLabel(app.status),
  },
  {
    key: 'trustUpdatedOn',
    label: 'Trust Updated On',
    render: app => (app.trustUpdatedOn ? formatDate(app.trustUpdatedOn) : null),
  },
  {
    key: 'eventSystemTime',
    label: 'Last Detected',
    render: app => (app.eventSystemTime ? formatDate(app.eventSystemTime) : null),
  },
  {
    key: 'fileSetsCount',
    label: 'File Set Imports',
    render: (app, counts) => count(counts?.fileSetsCount),
  },
  {
    key: 'filesCount',
    label: 'Files',
    render: (app, counts) => count(counts?.filesCount),
  },
  {
    key: 'impactedDevicesCount',
    label: 'WDAC Impacted Devices',
    render: (app, counts, { onOpenDevices }) => (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {count(counts?.impactedDevicesCount)}
        <Tooltip title="View devices" arrow>
          <IconButton size="small" onClick={onOpenDevices} sx={{ p: 0.25 }}>
            <Iconify icon="mdi:open-in-new" width={14} height={14} color="#87C8E0" />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
  },
];

const GeneralTab = () => {
  const { appId } = useParams();
  const { onEditStatus } = useOutletContext() ?? {};
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const [devicesOpen, setDevicesOpen] = useState(false);

  const { data: app } = useQuery(
    [WDAC_APP_QUERY_KEY, appId, customerEnvId],
    () => WDACResultsService.getWDACApp({ customerEnvId, globalApplicationVersionId: appId }),
    { enabled: !!appId && !!customerEnvId },
  );

  const { data: counts } = useQuery(
    [RESULTS_TAB_DATA_COUNTS_KEY, appId],
    () =>
      WDACResultsService.getAppWdacTabsDataCounts({
        globalApplicationVersionId: appId,
        customerEnvId,
      }),
    { enabled: !!appId && !!customerEnvId },
  );

  const isTrusted = app?.status === APPLICATION_TRUST_STATUS.Trusted;

  // The same read the import rows below make, so it costs nothing extra and follows status saves.
  const { data: fileSets } = useWin32AppFileSets({
    globalApplicationVersionId: appId,
    enabled: isTrusted,
  });
  const { data: policyState } = useWin32AppPolicy({
    globalApplicationVersionId: appId,
    enabled: isTrusted,
  });
  const anyImportEnabled = !!fileSets?.some(fileSet =>
    isCustomerEnvironmentApplicationEnabled(fileSet.status),
  );
  const hasActivePolicy = !!policyState?.policies?.some(policy => policy.isActive);
  // Generating writes its versions after the trust date in the same save, so a trust date past the
  // last generation means an installation import trusted it with no managed base to rebuild against.
  const trustedSinceGenerated =
    !!app?.trustUpdatedOn &&
    (!policyState?.lastGeneratedAt ||
      new Date(app.trustUpdatedOn) > new Date(policyState.lastGeneratedAt));
  // Trust saved before base policies were required can have imports and no catalog, so every gap
  // is called out until the artifacts are built.
  const artifactsGap = !isTrusted
    ? null
    : fileSets?.length && !anyImportEnabled
      ? 'noImportsEnabled'
      : !anyImportEnabled || !policyState
        ? null
        : !hasActivePolicy
          ? 'noBasePolicies'
          : trustedSinceGenerated
            ? 'staleArtifacts'
            : null;

  if (!app) {
    return null;
  }

  // The imports and the catalog are both set on the trust form's artifacts step, so these skip
  // straight to it.
  const openArtifacts = () => onEditStatus({ startOnArtifacts: true });

  return (
    <Stack gap={2}>
      <EntityStatusCard
        name={app.version ? `${app.name} (${app.version})` : app.name}
        status={app.status}
        note={app.notes}
        noteInTitle
        titleAdornment={
          artifactsGap ? <WarningIcon message={ARTIFACTS_GAPS[artifactsGap].warning} /> : null
        }
        // The catalog only exists for a trusted app, so its actions sit beside that status.
        actions={
          isTrusted ? (
            <Win32CatalogActions
              globalApplicationVersionId={appId}
              importsDisabled={artifactsGap === 'noImportsEnabled'}
              onUpdate={openArtifacts}
            />
          ) : null
        }
        onEdit={() => onEditStatus()}>
        {/* Which imports make up the app only matters once it is trusted, since that is what a
            catalog covers. */}
        {isTrusted ? (
          <>
            <Win32FileSetImports globalApplicationVersionId={appId} onEditImports={openArtifacts} />
            {/* With every import disabled there is nothing for a base policy to cover. */}
            {anyImportEnabled ? (
              <Win32TrustedBasePolicies
                globalApplicationVersionId={appId}
                onEditArtifacts={openArtifacts}
              />
            ) : null}
          </>
        ) : null}
      </EntityStatusCard>

      <DetectedActionsCard
        globalApplicationVersionId={appId}
        artifactsAction={artifactsGap ? ARTIFACTS_GAPS[artifactsGap] : null}
        onOpenArtifacts={openArtifacts}
      />

      <DetailsTable
        rows={APP_GENERAL_ROWS.map(row => ({
          key: row.key,
          label: row.label,
          value: row.render(app, counts, { onOpenDevices: () => setDevicesOpen(true) }),
        }))}
      />

      <TelemetryDevicesModal
        open={devicesOpen}
        onClose={() => setDevicesOpen(false)}
        globalApplicationVersionId={appId}
      />
    </Stack>
  );
};

export default GeneralTab;
